// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { InstrumentLookupQuickTreeNode } from '../QuickTree/InstrumentLookupQuickTreeNode';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { Instrument } from '@shared/commons/cache/Instrument';
import { DataCache } from '@shared/commons/DataCache';
import { InsDefSettings } from '@shared/commons/cache/InstrumentDefaults';
import { InstrumentLookupComparer } from '@shared/utils/Instruments/InstrumentLookupComparer';
import { InstrumentLookupType } from './InstrumentLookupManagerProps';
import { OptionType } from '@shared/utils/Instruments/OptionType';
import { SymbolLookupViewItemHelper } from '@shared/utils/Instruments/SymbolLookupViewItemHelper';
class _InstrumentLookupManager {
    constructor() {
        this._cachedStrikeList = {};
    }
    GetNodeLVL(node, lvl) {
        if (!isNullOrUndefined(node.parentNode)) {
            lvl = this.GetNodeLVL(node.parentNode, ++lvl);
        }
        return lvl;
    }
    PopulateNode(node, quickTree, instrumentItem, isOptionMasterMode) {
        const small = quickTree.isSmall;
        node.InstrumentNameText = instrumentItem.DisplayName();
        if (!small) {
            if (isOptionMasterMode && instrumentItem.ForwardBaseInstrument != null) {
                node.InstrumentTradingExchangeText = instrumentItem.ForwardBaseInstrument.TradingExchange;
            }
            else {
                node.InstrumentTradingExchangeText = instrumentItem.TradingExchange;
            }
            node.FlagImg = DataCache.CountryCache.GetFlagImage(instrumentItem.CountryId);
        }
        let descr = instrumentItem.DescriptionValue();
        if (!isValidString(descr) && !isNullOrUndefined(node.parentNode)) {
            descr = node.parentNode.InstrumentDescriptionText;
        } // #113025 for big
        node.InstrumentDescriptionText = descr;
        node.setInstrumentTypeDisplay(SymbolLookupViewItemHelper.getCorrectedInstrumentTypeForDisplay(instrumentItem.InstrType, instrumentItem.CFD));
    }
    PopulateNodeFutureOptionGroupNode(node, quickTree, name, description, instrumentItem) {
        const context = quickTree.DrawingContext;
        context.font = InstrumentLookupQuickTreeNode.Font;
        node.InstrumentNameText = name;
        node.InstrumentDescriptionText = description;
        node.setInstrumentTypeDisplay(SymbolLookupViewItemHelper.getCorrectedInstrumentTypeForDisplay(instrumentItem.InstrType, instrumentItem.CFD));
        const baseIns = instrumentItem.ForwardBaseInstrument;
        if (!isNullOrUndefined(baseIns)) {
            node.InstrumentTradingExchangeText = baseIns.TradingExchange;
            node.FlagImg = DataCache.CountryCache.GetFlagImage(baseIns.CountryId);
        }
    }
    PopulateNodeByStrikes(parentNode, cachedList, currentNameFilter, quickTree, changeCollapsed = true) {
        // TODO Refactoring UGLY ILACQTR.addChildNodesByInstrumentsArray
        if (parentNode.addChildNodesByInstrumentsArray) {
            return parentNode.addChildNodesByInstrumentsArray(parentNode.id, cachedList, currentNameFilter, quickTree);
        }
        const compareParams = { pattern: currentNameFilter, isSortByInstrumentType: true };
        cachedList = InstrumentLookupComparer.sortInstrumentList(cachedList, compareParams);
        parentNode.RemoveAllChildNodes();
        const childNodes = [];
        for (let i = 0, len = cachedList.length; i < len; i++) {
            const curIt = cachedList[i];
            const node = new InstrumentLookupQuickTreeNode(curIt.GetInteriorID());
            node.tag = curIt;
            if (quickTree.isMultiSelect && (parentNode.checked || parentNode.selected)) // #113317
             {
                node.checked = node.selected = true;
            }
            if (isValidString(currentNameFilter)) {
                node.textPattern = currentNameFilter;
            }
            this.PopulateNode(node, quickTree, curIt, false);
            if (!isValidString(node.InstrumentDescriptionText)) {
                node.InstrumentDescriptionText = parentNode.InstrumentDescriptionText;
            } // 113025+
            if (!currentNameFilter || this.isMatchByFilter(node)) // 113013 big
             {
                childNodes.push(node);
            }
        }
        parentNode.AddChildNodeRange(childNodes);
        if (changeCollapsed) {
            parentNode.collapsed = !parentNode.collapsed;
        }
        parentNode.skipAdding = true;
        quickTree.Draw(true);
        return true;
    }
    OptionProcess(currentWorkNode, optionInstrument, props) {
        var _a, _b, _c;
        const quickTree = props.quickTree;
        const underlier = optionInstrument.ForwardBaseInstrument;
        const instrumentName = (_a = underlier.SourceName) !== null && _a !== void 0 ? _a : underlier.DisplayName();
        const instrumentDescription = (_c = (_b = underlier.SourceDescription) !== null && _b !== void 0 ? _b : underlier.UnderlierDescription) !== null && _c !== void 0 ? _c : '';
        const newNode = this.checkCreateAddNode(currentWorkNode, instrumentName);
        if (!isNullOrUndefined(newNode)) {
            newNode.tag = 'group';
            newNode.sortingStop = true;
            newNode.setInstrumentTypeDisplay(SymbolLookupViewItemHelper.getCorrectedInstrumentTypeForDisplay(optionInstrument.InstrType, optionInstrument.CFD));
            if (!props.isInstrumentDefaults) {
                this.PopulateNodeFutureOptionGroupNode(newNode, quickTree, instrumentName, instrumentDescription, optionInstrument);
            }
        }
        currentWorkNode = currentWorkNode[instrumentName];
        return this.AddOptionEvent(currentWorkNode, optionInstrument, props);
    }
    AddOptionEvent(currentWorkNode, optionInstrument, props) {
        const underlier = optionInstrument.ForwardBaseInstrument;
        const quickTree = props.quickTree;
        const dataProvider = props.dataProvider;
        const currentNameFilter = props.currentNameFilter;
        const insType = SymbolLookupViewItemHelper.getCorrectedInstrumentTypeForDisplay(optionInstrument.InstrType, optionInstrument.CFD);
        const parentNode = currentWorkNode;
        // если у опциона родитель фьючерс
        if (!isNullOrUndefined(underlier) && (underlier.isFuturesSymbol || underlier.IsFakeNonFixed())) {
            const nodeName = underlier.DisplayName();
            // существует ли он в дереве?
            if (nodeName !== currentWorkNode.node.nodeText) {
                const node = this.checkCreateAddNode(currentWorkNode, nodeName);
                if (!isNullOrUndefined(node)) {
                    node.setInstrumentTypeDisplay(insType);
                    node.sortingStop = true;
                    if (!props.isInstrumentDefaults) {
                        this.PopulateNodeFutureOptionGroupNode(node, quickTree, nodeName, underlier.DescriptionValue(), optionInstrument);
                    }
                }
                // спускаемся ниже ExchangeName->InstrType->instrGroup->Option parentInstrument -> Option parentInstrument (Future)
                currentWorkNode = currentWorkNode[nodeName];
            }
        }
        if (props.isOptionMaster) {
            const node = currentWorkNode.node;
            node.textPattern = props.currentNameFilter;
            node.tag = optionInstrument;
            return parentNode;
        }
        if (optionInstrument.optionType === OptionType.Fake) {
            const node = currentWorkNode.node;
            node.tag = optionInstrument;
            node.AddChildNode(new InstrumentLookupQuickTreeNode('null'));
            node.DoCollapse = () => {
                if (!node.SkipRequest) {
                    const nullNode = node.childNodes[0];
                    if (quickTree.isMultiSelect && nullNode.selected) {
                        quickTree.addNodeToDict(nullNode, false);
                    }
                    node.RemoveAllChildNodes();
                    node.SkipRequest = true;
                    void dataProvider.getOption(optionInstrument).then((option) => {
                        const strikeList = dataProvider.getStrikesList(option);
                        const strikesByExpdates = new Map();
                        for (let i = 0; i < strikeList.length; i++) {
                            let strikes = strikesByExpdates.get(strikeList[i].ExpDate);
                            if (isNullOrUndefined(strikes)) {
                                strikes = [];
                                strikesByExpdates.set(strikeList[i].ExpDate, strikes);
                            }
                            strikes.push(strikeList[i]);
                        }
                        for (const expDate of strikesByExpdates.keys()) {
                            const strikes = strikesByExpdates.get(expDate);
                            const date = DateTimeUtils.formatDate(expDate, 'DD.MM.YYYY');
                            const newNode = this.checkCreateAddNode(currentWorkNode, date);
                            newNode.missAtUnCollapse = true;
                            newNode.sortingStop = true;
                            newNode.setInstrumentTypeDisplay(insType);
                            this.PopulateNodeFutureOptionGroupNode(newNode, quickTree, date, isValidArray(strikes) ? strikes[0].SeriesDescription : '', option);
                            newNode.InstrumentTradingExchangeText = newNode.FlagImg = null;
                            this.PopulateNodeByStrikes(newNode, strikes, currentNameFilter, quickTree, false);
                        }
                        node.collapsed = !node.collapsed;
                        quickTree.Draw(true);
                    });
                }
                else {
                    node.collapsed = !node.collapsed;
                }
            };
            return parentNode;
        }
        const date = DateTimeUtils.formatDate(optionInstrument.ExpDate, 'DD.MM.YYYY');
        // теперь дата экспирациив
        const newNode = this.checkCreateAddNode(currentWorkNode, date);
        if (!isNullOrUndefined(newNode)) {
            newNode.missAtUnCollapse = true;
            newNode.sortingStop = true;
            newNode.setInstrumentTypeDisplay(insType);
            if (!props.isInstrumentDefaults) {
                this.PopulateNodeFutureOptionGroupNode(newNode, quickTree, date, optionInstrument.SeriesDescription, optionInstrument);
            }
            newNode.InstrumentTradingExchangeText = newNode.FlagImg = null; // не нужен флаг и exchng поскольку серия - неторговая сущность #113027
        }
        // спускаемся ниже ExchangeName->InstrType->instrGroup->Option parentInstrument -> (parentInstrument.isFuturesSymbol? Option parentInstrument(Future) : "without")->ExpDate
        currentWorkNode = currentWorkNode[date];
        let needOpenStrike = false; // #113021
        if (!isNullOrUndefined(newNode)) {
            const cachedList = props.isInstrumentDefaults
                ? [optionInstrument]
                : this.tryGetCachedStrikeList(optionInstrument);
            if (!isNullOrUndefined(cachedList) && props.instrumentLookupType !== InstrumentLookupType.Small) {
                needOpenStrike = this.PopulateNodeByStrikes(newNode, cachedList, currentNameFilter, quickTree);
            }
            const lookupManager = this;
            newNode.DoCollapse = function () {
                const me = this; // TODO check: #93065 !me.childNodes.length  //#92878 !this.SkipRequest
                if (me.collapsed &&
                    dataProvider &&
                    DataCache.NonFixedList &&
                    !me.SkipRequest &&
                    !props.isInstrumentDefaults) {
                    const ins = me.tag;
                    const cachedList = lookupManager.tryGetCachedStrikeList(ins);
                    if (!isNullOrUndefined(cachedList)) {
                        lookupManager.PopulateNodeByStrikes(me, cachedList, currentNameFilter, quickTree);
                        if (newNode.selected) {
                            quickTree.addNodeToDict(newNode, true);
                            quickTree.raiseOnSelectionChanged();
                        }
                    }
                    else {
                        void dataProvider.GetNonFixedInstrumentStrikes(ins).then((list) => {
                            lookupManager.cacheStrikeList(list, ins);
                            lookupManager.PopulateNodeByStrikes(me, list, currentNameFilter, quickTree);
                            if (newNode.selected) {
                                quickTree.addNodeToDict(newNode, true);
                                quickTree.raiseOnSelectionChanged();
                            }
                        });
                    }
                }
                else {
                    me.collapsed = !me.collapsed;
                }
            };
            newNode.tag = optionInstrument;
        }
        const isLoadStrikesNode = (optionInstrument.StrikePrice === -1 || optionInstrument.StrikePrice === 0) &&
            optionInstrument.IsFakeNonFixed() &&
            !!optionInstrument.InstrDateSettingsList.length;
        const needOpen = (isValidString(props.currentNameFilter) && !isLoadStrikesNode) || needOpenStrike;
        currentWorkNode.node.collapsed = !needOpen;
        currentWorkNode.node.SkipRequest = needOpen;
        return currentWorkNode;
    }
    tryGetCachedStrikeList(ins) {
        var _a;
        const ID = ins ? ins.ContractID : null;
        let res = (_a = this._cachedStrikeList[ID]) !== null && _a !== void 0 ? _a : null;
        res = res !== null && res !== void 0 ? res : DataCache.GetInstrumentsByContractID(ID); // #113518,#113548
        return res;
    }
    cacheStrikeList(InsList, seriaIns) {
        let success = false;
        const ID = seriaIns ? seriaIns.ContractID : null;
        if (ID) {
            success = !!(this._cachedStrikeList[ID] = InsList);
        }
        return success;
    }
    fillTree(properties) {
        var _a, _b, _c, _d, _e, _f;
        const items = (_a = properties.instruments) !== null && _a !== void 0 ? _a : [];
        const selectedItem = (_b = properties.selectedInstruments) !== null && _b !== void 0 ? _b : null;
        const currentNameFilter = (_c = properties.currentNameFilter) !== null && _c !== void 0 ? _c : null;
        const quickTree = properties.quickTree;
        const selectedItemsMap = {};
        const selectedItemsIsArray = selectedItem instanceof Array;
        if (selectedItemsIsArray) {
            for (let i = 0; i < selectedItem.length; i++) {
                selectedItemsMap[selectedItem[i].GetInteriorID()] = selectedItem[i];
            }
        }
        let selectedNode = null;
        const nodeRangeTree = {};
        const hasFilter = currentNameFilter !== null;
        let filterString = '';
        if (hasFilter) {
            filterString = currentNameFilter.toLowerCase();
        }
        const insTypeNodeArr = [];
        const allExchangeIdText = Resources.getResource('ExchangeId.All');
        if (properties.isInstrumentDefaults) {
            const allExchangesNode = new InstrumentLookupQuickTreeNode(allExchangeIdText);
            allExchangesNode.collapsed = false;
            const allExchangesNodeTreeObj = { node: allExchangesNode };
            nodeRangeTree[allExchangeIdText] = allExchangesNodeTreeObj;
            const existingInstrumentTypes = properties.existingInstrumentTypes;
            for (let i = 0; i < existingInstrumentTypes.length; i++) {
                const instrumentType = existingInstrumentTypes[i];
                const insTypeText = Instrument.getTypeString(instrumentType);
                const insTypeNode = new InstrumentLookupQuickTreeNode(insTypeText);
                insTypeNode.setInstrumentTypeDisplay(instrumentType);
                insTypeNode.tag = InsDefSettings.INSTRUMENT_TYPE + instrumentType;
                allExchangesNodeTreeObj[insTypeText] = {
                    node: insTypeNode,
                    groups: {}
                };
                allExchangesNode.AddChildNode(insTypeNode);
                insTypeNodeArr.push(insTypeNode);
            }
        }
        const len = items.length;
        for (let i = 0; i < len; i++) {
            const curItem = items[i];
            // instrGroup
            const instrGroup = curItem.getInstrumentTypeName();
            // ExchangeName   TradingExchange
            const curItemExchangeValue = properties.isInstrumentDefaults
                ? allExchangeIdText
                : curItem.isOptionSymbol
                    ? curItem.ForwardBaseInstrument.TradingExchange
                    : curItem.TradingExchange;
            // InstrType
            const curItemInstrTypeValue = curItem.CFD ? InstrumentTypes.EQUITIES_CFD : curItem.InstrType;
            if (((_d = properties.instrTypesToIgnore) === null || _d === void 0 ? void 0 : _d.length) && properties.instrTypesToIgnore.includes(curItemInstrTypeValue)) {
                continue;
            }
            const curItemInstrTypeText = curItem.getTypeString();
            let curItemText = curItem.DisplayName();
            const descr = curItem.DescriptionValue();
            if (descr) {
                curItemText += ' (' + descr + ')';
            }
            if (hasFilter && (!DataCache.NonFixedList || properties.isInstrumentDefaults)) {
                const curItemFindStr = curItemText.toLowerCase();
                if (!curItemFindStr.includes(filterString)) {
                    continue;
                }
            }
            // todo blizzard sc2
            const fullContains = curItem.DisplayShortName().toLocaleLowerCase() === filterString;
            // нода в которую закинем инструмент
            let currentWorkNode = null;
            // проверяем ExchangeName ,если нет добавляем
            this.checkCreateAddNode(nodeRangeTree, curItemExchangeValue, true);
            // сейчас это ExchangeName нода
            currentWorkNode = nodeRangeTree[curItemExchangeValue];
            currentWorkNode.node.tag = 'exchange';
            // проверяем InstrType ,если нет добавляем
            const insType = SymbolLookupViewItemHelper.getCorrectedInstrumentTypeForDisplay(curItem.InstrType, curItem.CFD);
            let newNode = null;
            newNode = this.checkCreateAddNode(currentWorkNode, curItemInstrTypeText);
            if (!isNullOrUndefined(newNode)) {
                // set images
                newNode.setInstrumentTypeDisplay(insType);
                newNode.tag = curItemInstrTypeValue;
                // заполняем массив всех InstrType
                insTypeNodeArr.push(newNode);
            }
            // спускаемся ниже ExchangeName->InstrType
            currentWorkNode = currentWorkNode[curItemInstrTypeText];
            // смотрим группу. нету? добавляем.
            newNode = this.checkCreateAddNode(currentWorkNode, instrGroup);
            if (!isNullOrUndefined(newNode)) {
                if (fullContains) {
                    newNode.FullContainsCount++;
                }
                newNode.tag = 'group';
                newNode.SpecificTag = 'instgroup';
                newNode.setInstrumentTypeDisplay(insType);
                newNode.sortingStop = true;
            }
            else {
                const tmpNode = currentWorkNode[instrGroup];
                if (!isNullOrUndefined(tmpNode) && fullContains) {
                    tmpNode.node.FullContainsCount++;
                }
            }
            // спускаемся ниже ExchangeName->InstrType->instrGroup
            currentWorkNode = currentWorkNode[instrGroup];
            if (curItem.isFuturesSymbol) {
                const name = curItem.SourceName;
                const description = isValidString(curItem.SourceDescription) ? curItem.SourceDescription : '';
                const newNode = this.checkCreateAddNode(currentWorkNode, name);
                if (!isNullOrUndefined(newNode)) {
                    newNode.tag = 'group';
                    newNode.sortingStop = true;
                    newNode.setInstrumentTypeDisplay(insType);
                    if (!properties.isInstrumentDefaults) {
                        this.PopulateNodeFutureOptionGroupNode(newNode, quickTree, name, description, curItem);
                    }
                }
                currentWorkNode = currentWorkNode[name];
            }
            else if (curItem.isOptionSymbol &&
                !isNullOrUndefined(curItem.ForwardBaseInstrument) &&
                (!properties.isOptionMaster || curItem.ForwardBaseInstrument.isFuturesSymbol)) {
                currentWorkNode = this.OptionProcess(currentWorkNode, curItem, properties);
            }
            // создаём ноду инструмента
            const nodeText = curItem.optionType === OptionType.Fake || properties.isOptionMaster
                ? curItem.DisplayName()
                : curItem.GetInteriorID();
            let node = null;
            const parentNode = currentWorkNode.node;
            node = parentNode.GetChildNodeByText(nodeText);
            if (!node) {
                node = new InstrumentLookupQuickTreeNode(nodeText);
                if (currentNameFilter) {
                    node.textPattern = currentNameFilter;
                }
                node.tag = curItem;
                // добавляем её в ноду которую (нашли||сформировали)
                if (!parentNode.skipAdding ||
                    node.nodeText.indexOf('null') === -1) // if тут для того чтоб не добавлять времен. опцион. инстр. когда в кеше уже имеем список страйков из [421]
                 {
                    parentNode.AddChildNode(node);
                }
                this.PopulateNode(node, quickTree, curItem, properties.isOptionMaster);
            }
            if (selectedItemsIsArray) {
                if (!isNullOrUndefined(selectedItemsMap[nodeText])) {
                    quickTree.unCollapseAllParents(node);
                    quickTree.addNodeToDict(node, true);
                }
            }
            else if (selectedItem === curItem ||
                (properties.isOptionMaster && Instrument.IsEqualOptionInstruments(curItem, selectedItem))) {
                selectedNode = node;
                node.selected = true;
            }
        }
        const keys = Object.keys(nodeRangeTree);
        const arrToWork = [];
        for (let i = 0; i < keys.length; i++) {
            const node = nodeRangeTree[keys[i]].node;
            node.sortingtoLowerCaseKey = (_f = (_e = node.nodeText) === null || _e === void 0 ? void 0 : _e.toLowerCase()) !== null && _f !== void 0 ? _f : '';
            if (!node.nodeLevel || !isValidString(currentNameFilter) || this.isMatchByFilter(node)) {
                arrToWork.push(node);
            }
        }
        arrToWork.sort(function (a, b) {
            return a.sortingtoLowerCaseKey > b.sortingtoLowerCaseKey ? 1 : -1;
        });
        this.sortInstrumentTypeChildNodes(arrToWork);
        // TODO check after all
        // if (!isInstrumentsDefaultsMode)
        this.removeUnnecessaryGroupChildNodes(insTypeNodeArr);
        quickTree.Clear();
        quickTree.AddNodeRange(arrToWork);
        if (!selectedItemsIsArray) {
            if (hasFilter) {
                quickTree.unCollapseAllPosible(DataCache.NonFixedList);
            }
            if (!isNullOrUndefined(selectedNode)) {
                quickTree.setSelectedNode(selectedNode);
                quickTree.goToSelectedNode();
            }
        }
    }
    checkCreateAddNode(dictionary, key, skipAdd) {
        let newNode = null;
        if (isNullOrUndefined(dictionary[key])) {
            dictionary[key] = {};
            newNode = new InstrumentLookupQuickTreeNode(key);
            dictionary[key].node = newNode;
            if (!skipAdd) {
                dictionary.node.AddChildNode(newNode);
            }
        }
        return newNode;
    }
    removeUnnecessaryGroupChildNodes(insTypeNodes) {
        const len = !isNullOrUndefined(insTypeNodes) ? insTypeNodes.length : 0;
        for (let i = 0; i < len; i++) {
            const insTypeNode = insTypeNodes[i];
            const groupNodes = insTypeNode.childNodes;
            if (groupNodes.length === 1) {
                const instrumentNodes = groupNodes[0].childNodes;
                insTypeNode.childNodes = instrumentNodes;
            }
        }
    }
    sortInstrumentTypeChildNodes(exchangeNodes) {
        const len = isValidArray(exchangeNodes) ? exchangeNodes.length : 0;
        for (let i = 0; i < len; i++) {
            const childNodes = exchangeNodes[i].childNodes;
            const firstChild = childNodes.length > 0 ? childNodes[0] : null;
            if (!isNullOrUndefined(firstChild)) {
                childNodes.sort(firstChild.SpecificTag ? this.instrumentSubGroupsNodeComparator : this.instrumentTypeNodeComparator);
                if (!firstChild.sortingStop) {
                    this.sortInstrumentTypeChildNodes(childNodes);
                }
            }
        }
    }
    instrumentSubGroupsNodeComparator(n1, n2) {
        if (n1.FullContainsCount === n2.FullContainsCount) {
            return n1.nodeText > n2.nodeText ? 1 : -1;
        } // Check! Perhaps better use String.prototype.localeCompare() ? Not sure cuz lots of differences: 'a' > 'A' == true => return 1, but 'a'.localeCompare('A') == -1;     'a' > 'A' == true(1), but 'a'.localeCompare('A') == -1;    'Aa' > 'aA' == false(in old way such returns -1), but 'Aa'.localeCompare('aA') == 1;        'a' > 'a' == false (=> returns -1), but 'a'.localeCompare('a') == 0 (however, no matter here, I think);         'aa' > 'aAa' == true(returns 1), but 'aa'.localeCompare('aAa') == -1  etc.        Despite it has lots of cases with equal results such as 'Aaa'.localeCompare('Aa') == 1 == 'Aaa' > 'Aa' also returns 1;             'a'.localeCompare('b') == -1 == ('a' > 'b' ? 1 : -1)    etc.
        return n1.FullContainsCount > n2.FullContainsCount ? -1 : 1; // May replace for Math.sign(n2.FullContainsCount - n1.FullContainsCount) ? see example below
    }
    // this.instrumentSubGroupsNodeComparator = function (n1, n2)
    // {
    //     let sign = Math.sign(n2.FullContainsCount - n1.FullContainsCount)
    //     if (!sign)
    //         return n1.nodeText > n2.nodeText ? 1 : -1                // check, perhaps better use String.prototype.localeCompare() ? Not sure cuz lots of differences: 'a' > 'A' == true => return 1, but 'a'.localeCompare('A') == -1;     'a' > 'A' == true(1), but 'a'.localeCompare('A') == -1;    'Aa' > 'aA' == false(in old way such returns -1), but 'Aa'.localeCompare('aA') == 1;        'a' > 'a' == false (=> returns -1), but 'a'.localeCompare('a') == 0 (however, no matter here, I think);         'aa' > 'aAa' == true(returns 1), but 'aa'.localeCompare('aAa') == -1  etc.        Despite it has lots of cases with equal results such as 'Aaa'.localeCompare('Aa') == 1 == 'Aaa' > 'Aa' also returns 1;             'a'.localeCompare('b') == -1 == ('a' > 'b' ? 1 : -1)    etc.
    //     return sign
    // }
    instrumentTypeNodeComparator(n1, n2) {
        return n1.nodeText > n2.nodeText ? 1 : -1;
    }
    isMatchByFilter(node) {
        if (!isValidString(node.textPattern)) {
            return false;
        }
        const matchText = node.textPattern.toLowerCase();
        if (isValidString(node.InstrumentNameText) && node.InstrumentNameText.toLowerCase().includes(matchText)) {
            return true;
        }
        if (isValidString(node.InstrumentDescriptionText) &&
            node.InstrumentDescriptionText.toLowerCase().includes(matchText)) {
            return true;
        }
        return false;
    }
}
export const InstrumentLookupManager = new _InstrumentLookupManager();
